<template>
  <v-expansion-panel :disabled="isCreate">
    <v-expansion-panel-header class="my-4">
      <div>
        <h2 class="font-weight-regular pb-4"><span v-html="$t('page-rules.base-rule.section-check-in-days.title')"></span> <span v-if="bookingRule['@type'] === 'RollingBookingRule'" class="red--text">*</span></h2>
        <p v-html="$t('page-rules.base-rule.section-check-in-days.intro')" class="mb-0"></p>
      </div>
    </v-expansion-panel-header>
    <v-expansion-panel-content class="text-right mb-4">
      <v-divider class="py-4"></v-divider>

      <v-simple-table style="width: 100%;">
        <template v-slot:default>
          <thead>
            <tr>
              <th></th>
              <th>
                <v-switch :input-value="hasAllCheckInDaysToggled" class="place-rules__any-day-switch" @change="toggleAllCheckInDays()" :label="$t('page-rules.base-rule.section-check-in-days.fields.check-in.label')" :title="$t('global-terms.select-all')"></v-switch>
              </th>
              <th>
                <v-switch :input-value="hasAllCheckOutDaysToggled" class="place-rules__any-day-switch" @change="toggleAllCheckOutDays()" :label="$t('page-rules.base-rule.section-check-in-days.fields.check-out.label')" :title="$t('global-terms.select-all')"></v-switch>
              </th>
              <th class="text-left">
                {{ $t('page-rules.base-rule.section-check-in-days.fields.minimum-stay.label') }}
                <primary-tool-tip>{{ $t('page-rules.base-rule.section-check-in-days.fields.minimum-stay.info-tip') }}</primary-tool-tip>
              </th>
              <th class="text-left">
                {{ $t('page-rules.base-rule.section-check-in-days.fields.maximum-stay.label') }}
                <primary-tool-tip>{{ $t('page-rules.base-rule.section-check-in-days.fields.maximum-stay.info-tip') }}</primary-tool-tip>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="day in daysOfWeek" :key="day.day">
              <td align="left">{{ localeDay(day.name) | capitalise }}</td>
              <td>
                <v-switch :inputValue="daySelected(day)" @change="toggleCheckIn(day)"></v-switch>
              </td>
              <td>
                <v-switch :inputValue="mutatableBookingRule.stayLengths[day.dow][2]" @change="toggleCheckOut(day)"></v-switch>
              </td>
              <td>
                <v-text-field
                  v-model="mutatableBookingRule.stayLengths[day.dow][0]"
                  dense
                  flat
                  readonly
                  prepend-inner-icon="mdi-minus-circle"
                  append-icon="mdi-plus-circle"
                  style="width: 100px;"
                  class="place-rules__select"
                  @click:append="adjustDay(day, 0, 'plus')"
                  @click:prepend-inner="adjustDay(day, 0, 'minus')"
                  v-if="daySelected(day)"
                ></v-text-field>
              </td>
              <td>
                <v-text-field
                  v-model="mutatableBookingRule.stayLengths[day.dow][1]"
                  dense
                  flat
                  readonly
                  prepend-inner-icon="mdi-minus-circle"
                  append-icon="mdi-plus-circle"
                  style="width: 100px;"
                  class="place-rules__select"
                  @click:append="adjustDay(day, 1, 'plus')"
                  @click:prepend-inner="adjustDay(day, 1, 'minus')"
                  v-if="daySelected(day)"
                ></v-text-field>
              </td>
            </tr>
            <tr>
              <td align="left">
                <v-btn small color="primary" outlined @click="clearDays()" v-html="$t('global-terms.clear-all')"></v-btn>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <v-btn @click.stop="save()" color="primary" large :loading="saving" :disabled="saving || invalidCheckInData" v-html="$t('global-terms.save-continue')"></v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>
<script>
import { isNil, clone } from 'ramda'
import { mapActions } from 'pinia'
import PrimaryToolTip from '@/components/tooltips/PrimaryToolTip'
import { useBookingRuleStore } from '@/stores'

export default {
  data () {
    return {
      saving: false,
      mutatableBookingRule: this.clone(this.bookingRule),
      defaultMinValue: 1,
      defaultMaxValue: 30,
      defaultUnsetValue: 0,
      maxDayRange: 30,
      maxWeekRange: 28,
      step: 1,
      maxStep: 7,
      daysOfWeek: [
        {
          dow: 1,
          name: 'Monday',
        },
        {
          dow: 2,
          name: 'Tuesday',
        },
        {
          dow: 3,
          name: 'Wednesday',
        },
        {
          dow: 4,
          name: 'Thursday',
        },
        {
          dow: 5,
          name: 'Friday',
        },
        {
          dow: 6,
          name: 'Saturday',
        },
        {
          dow: 0,
          name: 'Sunday',
        },
      ],
    }
  },
  props: {
    bookingRule: {
      type: Object,
      required: true,
    },
    isCreate: {
      type: Boolean,
      required: true,
    },
    isEdit: {
      type: Boolean,
      required: true,
    },
    isFinalized: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    invalidCheckInData () {
      const validCheckInDays = this.mutatableBookingRule.stayLengths.filter(([min, max]) => min > 0 && max > 0 && max >= min)
      const validCheckOutDays = this.mutatableBookingRule.stayLengths.filter(([_, __, checkout]) => checkout > 0)
      return validCheckInDays.length === 0 || validCheckOutDays.length === 0
    },
    hasAllCheckInDaysToggled () {
      return this.mutatableBookingRule.stayLengths.filter(([min]) => {
        return min > 0
      }).length === 7
    },
    hasAllCheckOutDaysToggled () {
      return this.mutatableBookingRule.stayLengths.filter(([_, __, checkOut]) => {
        return checkOut === 1
      }).length === 7
    },
  },
  methods: {
    clone,
    ...mapActions(useBookingRuleStore, ['httpUpdateRollingBookingRule', 'httpUpdateSeasonalBookingRule']),
    isNil,
    adjustDay (day, type, adjustment) {
      let currentValue = this.mutatableBookingRule.stayLengths[day.dow][type]

      // get the alternative value (i.e. if 'min' then we need to get the 'max' values)
      const altType = type === 0 ? 1 : 0
      const altCurrentValue = this.mutatableBookingRule.stayLengths[day.dow][altType]

      // apply the step
      let newValue = (currentValue += adjustment === 'plus' ? this.step : -this.step)

      if (currentValue < this.step) {
        // make sure we don't go below the step
        newValue = this.step
      } else if (currentValue > this.maxDayRange) {
        newValue = this.maxDayRange
      } else if (
        // ensure we can't go above or below the alternative value
        (altType === 0 && newValue < altCurrentValue) ||
        (altType === 1 && newValue > altCurrentValue)
      ) {
        newValue = altCurrentValue
      }

      if (type === 0 && newValue > this.maxStep) {
        // make sure we don't go over the max step for min value
        newValue = this.maxStep
      }

      this.mutatableBookingRule.stayLengths[day.dow][type] = newValue

      // update the object so it is reactive
      this.mutatableBookingRule = this.clone(this.mutatableBookingRule)
    },
    toggleAllCheckInDays (selected) {
      if (typeof selected === 'undefined') {
        selected = !this.hasAllCheckInDaysToggled
      }

      this.daysOfWeek.forEach((day) => this.toggleCheckIn(day, selected))
    },
    toggleCheckIn (day, force = null) {
      const selected = !this.daySelected(day)
      const minValue = this.mutatableBookingRule.stayLengths[day.dow][0]

      if ((selected && isNil(force)) || force === true) {
        // if the toggle is set to active or it is forced true

        // check if a value has already been set and ignore if it has
        if (minValue === 0 || isNil(minValue)) {
          // check if a previous row has already been set and copy that value
          const prevStayLength = this.mutatableBookingRule.stayLengths.find((lengthArray) => {
            return lengthArray[0] > 0
          })

          this.mutatableBookingRule.stayLengths[day.dow][0] = !isNil(prevStayLength) ? prevStayLength[0] : this.defaultMinValue
          this.mutatableBookingRule.stayLengths[day.dow][1] = !isNil(prevStayLength) ? prevStayLength[1] : this.defaultMaxValue
        }
      } else if ((!selected && isNil(force)) || force === false) {
        // if the toggle is set to inactive or it is forced false
        this.mutatableBookingRule.stayLengths[day.dow][0] = this.defaultUnsetValue
        this.mutatableBookingRule.stayLengths[day.dow][1] = this.defaultUnsetValue
      }

      // update the object so it is reactive
      this.mutatableBookingRule = this.clone(this.mutatableBookingRule)
    },
    toggleAllCheckOutDays (selected) {
      if (typeof selected === 'undefined') {
        selected = !this.hasAllCheckOutDaysToggled
      }

      this.daysOfWeek.forEach((day) => this.toggleCheckOut(day, selected))
    },
    toggleCheckOut (day, force = null) {
      const checkOutValue = this.mutatableBookingRule.stayLengths[day.dow][2]
      let newCheckOutValue = 0

      if ((checkOutValue !== 1 && force === null) || force === true) {
        newCheckOutValue = 1
      } else if ((checkOutValue === 1 && force === null) || force === false) {
        newCheckOutValue = 0
      }

      this.mutatableBookingRule.stayLengths[day.dow][2] = newCheckOutValue
      this.mutatableBookingRule = this.clone(this.mutatableBookingRule)
    },
    clearDays () {
      // de-select all days
      this.toggleAllCheckInDays(false)
      this.toggleAllCheckOutDays(false)
    },
    localeDay (day) {
      return this.$d(this.moment().day(day), 'weekday')
    },
    daySelected (day) {
      return this.mutatableBookingRule.stayLengths[day.dow][0] !== 0 && this.mutatableBookingRule.stayLengths[day.dow][1] > 0
    },
    save () {
      this.saving = true
      if (this.isEdit) {
        const bookingRuleClone = this.clone(this.mutatableBookingRule)
        delete bookingRuleClone.rates
        delete bookingRuleClone.bookingRuleUnits

        if (this.bookingRule['@type'] === 'RollingBookingRule') {
          this.httpUpdateRollingBookingRule({ rollingBookingRule: bookingRuleClone }).then(() => {
            this.saving = false
          })
        } else if (this.bookingRule['@type'] === 'SeasonalBookingRule') {
          this.httpUpdateSeasonalBookingRule({ seasonalBookingRule: bookingRuleClone }).then(() => {
            this.saving = false
          })
        }

        if (!this.isFinalized) {
          this.$emit('autoScroll', 2)

          this.$nextTick(() => {
            this.$vuetify.goTo('#third', {
              duration: 1000,
              offset: 25,
            })
          })
        }
      }
    },
  },
  components: {
    PrimaryToolTip,
  },
  watch: {
    bookingRule (newVal) {
      this.mutatableBookingRule = newVal
    },
  },
}
</script>
